import React from 'react';
import { useTranslation } from 'react-i18next';
import {
  Container,
  Typography,
  Box,
  Grid,
  Card,
  CardContent,
  Chip,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
} from '@mui/material';
import EmailIcon from '@mui/icons-material/Email';
import PhoneIcon from '@mui/icons-material/Phone';
import LocationOnIcon from '@mui/icons-material/LocationOn';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import ContactForm from '../forms/ContactForm';
import { SEOHead, pageSEO } from '../../utils/seo';

/**
 * Contact page component with contact form and company information
 * Integrates with ContactForm component for lead capture
 */
const ContactPage: React.FC = () => {
  const { t } = useTranslation('marketing');

  const contactMethods = [
    {
      icon: <EmailIcon color="primary" />,
      title: t('contact_method_email_title'),
      description: t('contact_method_email_desc'),
      value: t('contact_method_email_value'),
      href: `mailto:${t('contact_method_email_value')}`,
    },
    {
      icon: <LocationOnIcon color="primary" />,
      title: t('contact_method_location_title'),
      description: t('contact_method_location_desc'),
      value: t('contact_method_location_value'),
      href: '#',
    },
    {
      icon: <AccessTimeIcon color="primary" />,
      title: t('contact_method_hours_title'),
      description: t('contact_method_hours_desc'),
      value: t('contact_method_hours_value'),
      href: '#',
    },
  ];

  const whyChooseUs = [
    t('contact_why_choose_1'),
    t('contact_why_choose_2'),
    t('contact_why_choose_3'),
    t('contact_why_choose_4'),
    t('contact_why_choose_5'),
    t('contact_why_choose_6'),
  ];

  return (
    <>
      <SEOHead {...pageSEO.contact} />
      <Box data-testid="contact-page">
      {/* Hero Section */}
      <Box
        sx={{
          background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
          color: 'white',
          py: { xs: 8, md: 12 },
          textAlign: 'center',
        }}
      >
        <Container maxWidth="lg">
          <Typography
            variant="h1"
            component="h1"
            data-testid="contact-hero-title"
            sx={{
              fontSize: { xs: '2.5rem', md: '3.5rem' },
              fontWeight: 700,
              mb: 2,
            }}
          >
            {t('contact_page_hero_title')}
          </Typography>
          <Typography
            variant="h5"
            component="p"
            data-testid="contact-hero-subtitle"
            sx={{
              fontSize: { xs: '1.1rem', md: '1.25rem' },
              opacity: 0.9,
              maxWidth: '600px',
              mx: 'auto',
              mb: 4,
            }}
          >
            {t('contact_page_hero_subtitle')}
          </Typography>
          <Chip
            label={t('contact_page_free_consultation')}
            data-testid="free-consultation-chip"
            sx={{
              bgcolor: 'rgba(255, 255, 255, 0.2)',
              color: 'white',
              fontWeight: 600,
              px: 2,
              py: 1,
            }}
          />
        </Container>
      </Box>

      <Container maxWidth="lg" sx={{ py: { xs: 6, md: 10 } }}>
        <Box
          sx={{
            display: 'flex',
            flexDirection: { xs: 'column', lg: 'row' },
            gap: 6,
          }}
        >
          {/* Contact Form Section */}
          <Box sx={{ flex: { xs: '1', lg: '2' } }}>
            <Box sx={{ mb: 4 }}>
              <Typography
                variant="h2"
                component="h2"
                data-testid="contact-form-title"
                sx={{
                  fontSize: { xs: '2rem', md: '2.5rem' },
                  fontWeight: 600,
                  mb: 2,
                  color: 'text.primary',
                }}
              >
                {t('contact_page_form_title')}
              </Typography>
              <Typography
                variant="body1"
                color="text.secondary"
                data-testid="contact-form-description"
                sx={{ fontSize: '1.1rem', mb: 4 }}
              >
                {t('contact_page_form_description')}
              </Typography>
            </Box>

            {/* Contact Form Component */}
            <ContactForm />
          </Box>

          {/* Contact Information Sidebar */}
          <Box sx={{ flex: { xs: '1', lg: '1' } }}>
            {/* Contact Methods */}
            <Card
              elevation={2}
              data-testid="contact-methods-card"
              sx={{ mb: 4, p: 3 }}
            >
              <Typography
                variant="h6"
                component="h3"
                data-testid="contact-methods-title"
                sx={{ fontWeight: 600, mb: 3 }}
              >
                {t('contact_page_methods_title')}
              </Typography>
              
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
                {contactMethods.map((method, index) => (
                  <Box
                    key={index}
                    component="a"
                    href={method.href}
                    data-testid={`contact-method-${index}`}
                    sx={{
                      display: 'flex',
                      alignItems: 'flex-start',
                      textDecoration: 'none',
                      color: 'inherit',
                      p: 2,
                      borderRadius: 2,
                      transition: 'all 0.2s ease-in-out',
                      '&:hover': {
                        backgroundColor: 'rgba(102, 126, 234, 0.04)',
                        transform: 'translateY(-2px)',
                      },
                    }}
                  >
                    <Box sx={{ mr: 2, mt: 0.5 }}>
                      {method.icon}
                    </Box>
                    <Box>
                      <Typography
                        variant="subtitle2"
                        sx={{ fontWeight: 600, mb: 0.5 }}
                      >
                        {method.title}
                      </Typography>
                      <Typography
                        variant="body2"
                        color="text.secondary"
                        sx={{ mb: 0.5, fontSize: '0.875rem' }}
                      >
                        {method.description}
                      </Typography>
                      <Typography
                        variant="body2"
                        color="primary.main"
                        sx={{ fontWeight: 500 }}
                      >
                        {method.value}
                      </Typography>
                    </Box>
                  </Box>
                ))}
              </Box>
            </Card>

            {/* Why Choose Us */}
            <Card
              elevation={2}
              data-testid="why-choose-us-card"
              sx={{ p: 3 }}
            >
              <Typography
                variant="h6"
                component="h3"
                data-testid="why-choose-us-title"
                sx={{ fontWeight: 600, mb: 3 }}
              >
                {t('contact_why_choose_title')}
              </Typography>
              
              <List dense>
                {whyChooseUs.map((benefit, index) => (
                  <ListItem
                    key={index}
                    data-testid={`benefit-${index}`}
                    sx={{ px: 0, py: 1 }}
                  >
                    <ListItemIcon sx={{ minWidth: 32 }}>
                      <CheckCircleIcon
                        color="success"
                        sx={{ fontSize: '1.2rem' }}
                      />
                    </ListItemIcon>
                    <ListItemText
                      primary={benefit}
                      primaryTypographyProps={{
                        fontSize: '0.9rem',
                        color: 'text.secondary',
                      }}
                    />
                  </ListItem>
                ))}
              </List>
            </Card>
          </Box>
        </Box>
      </Container>

      {/* Call-to-Action Footer */}
      <Box
        sx={{
          bgcolor: 'grey.50',
          py: { xs: 6, md: 8 },
          textAlign: 'center',
        }}
      >
        <Container maxWidth="md">
          <Typography
            variant="h4"
            component="h2"
            data-testid="contact-cta-title"
            sx={{
              fontWeight: 600,
              mb: 2,
              color: 'text.primary',
            }}
          >
            {t('contact_cta_title')}
          </Typography>
          <Typography
            variant="body1"
            color="text.secondary"
            data-testid="contact-cta-description"
            sx={{
              fontSize: '1.1rem',
              mb: 4,
              maxWidth: '500px',
              mx: 'auto',
            }}
          >
            {t('contact_cta_description')}
          </Typography>
        </Container>
      </Box>
    </Box>
    </>
  );
};

export default ContactPage;